import type { CodeString } from '@/consts/codes';
import type { MenuItem } from '@/consts/navigation';
import type { TranslationKey } from '@/i18n/i18n';

interface RouteInfo {
  route?: string;
  code?: CodeString;
  label: TranslationKey;
  parent?: TranslationKey;
  parentChain?: TranslationKey[];
  isRedirectOnly?: boolean;
}

export const findMatchingRoute = (pathname: string, routes: RouteInfo[]): RouteInfo | undefined => {
  let match = routes.find((r) => r.route === pathname);

  match ??= routes.find((r) => {
    const pattern = r.route?.replace(/:\w+/g, '[^/]+');
    return pattern ? new RegExp(`^${pattern}$`).test(pathname) : false;
  });

  return match;
};

const buildRoutesRecursive = (item: MenuItem, parentChain: TranslationKey[] = []): RouteInfo[] => {
  const routes: RouteInfo[] = [];
  const currentChain = [...parentChain, item.text];

  if (item.to) {
    const hasChildren = Boolean(item.children?.length);
    routes.push({
      route: item.to,
      label: item.text,
      code: item.code,
      parent: parentChain[parentChain.length - 1],
      parentChain: parentChain.length > 0 ? parentChain : undefined,
      isRedirectOnly: hasChildren
    });
  }

  if (item.children) {
    for (const child of item.children) {
      routes.push(...buildRoutesRecursive(child, currentChain));
    }
  }

  return routes;
};

export const buildRouteMapping = (menu: MenuItem[]): RouteInfo[] => {
  return menu.flatMap<RouteInfo>((section) => {
    if (section.children) {
      return section.children.flatMap((child) => buildRoutesRecursive(child, [section.text]));
    }

    return section.to ? [{ label: section.text, route: section.to }] : [];
  });
};
